import { useEffect, useState } from "react";

import { AGENCY_INCLUDED_SEATS } from "features/subscriptions/constants";
import { ManageSubscriptionContextProvider } from "features/subscriptions/context/ManageSubscriptionContext";
import useAgencyPurchaseMinSeats from "features/subscriptions/hooks/useAgencyPurchaseMinSeats";
import type useManageSubscriptionSteps from "features/subscriptions/hooks/useManageSubscriptionSteps";
import usePricingOptions from "features/subscriptions/hooks/usePricingOptions";
import type { MixpanelSubscriptionSource } from "features/subscriptions/types";

import type {
  AgencySubscription,
  PricingPeriodInfo,
  RentedSubscription,
  Subscription,
  SubscriptionType,
} from "common/types";

import ManageSubscription from "./ManageSubscription";

type UseManageSubscriptionStepsType = ReturnType<
  typeof useManageSubscriptionSteps
>;

interface ManageSubscriptionProps {
  initialState:
    | {
        subscription: AgencySubscription | Subscription | RentedSubscription;
        seats: number;
      }
    | SubscriptionType;
  step: UseManageSubscriptionStepsType["manageSubscriptionStep"];
  goToStep: UseManageSubscriptionStepsType["goToManageSubscriptionStep"];
  mixpanelSource: MixpanelSubscriptionSource;
}

export default function ManageSubscriptionContainer({
  initialState,
  step,
  goToStep,
  mixpanelSource,
}: ManageSubscriptionProps) {
  const hasExistingSubscription = typeof initialState !== "string";
  const subscriptionType = hasExistingSubscription
    ? initialState.subscription.type
    : initialState;

  const hasActiveSubscription =
    hasExistingSubscription && initialState.subscription.status !== "cancelled";
  const isAgencyPurchase =
    subscriptionType === "agency" && !hasActiveSubscription;

  const { minSeats: agencyPurchaseMinSeats } =
    useAgencyPurchaseMinSeats(isAgencyPurchase);

  const [seatQuantity, setSeatQuantity] = useState(() => {
    //if there is no existing subscription, we use defaults
    if (
      typeof initialState === "string" ||
      initialState.subscription.status === "cancelled"
    ) {
      if (subscriptionType === "agency") {
        return AGENCY_INCLUDED_SEATS;
      }
      return 1;
    }
    //if there is an existing subscription, return the existing subscription's seats
    return (
      initialState.seats ??
      (subscriptionType === "agency" ? AGENCY_INCLUDED_SEATS : 1)
    );
  });

  const { pricingOptions } = usePricingOptions(
    subscriptionType,
    hasExistingSubscription ? initialState.subscription : undefined,
  );

  const [pricingPeriodInfo, setPricingPeriodInfo] = useState<PricingPeriodInfo>(
    () => {
      if (hasExistingSubscription) {
        const { period_unit, period } = initialState.subscription.item_price;
        return { period, period_unit };
      }
      if (pricingOptions.length) {
        const { period_unit, period } = pricingOptions[0].plan;
        return { period, period_unit };
      }
    },
  );

  const pricing =
    pricingPeriodInfo &&
    pricingOptions.find(
      ({ plan }) =>
        plan.period === pricingPeriodInfo.period &&
        plan.period_unit === pricingPeriodInfo.period_unit,
    );

  useEffect(() => {
    if (!pricingPeriodInfo && pricingOptions.length) {
      const { period_unit, period } = pricingOptions[0].plan;
      setPricingPeriodInfo({ period, period_unit });
    }
  }, [pricingOptions]);

  useEffect(() => {
    if (!isAgencyPurchase) return;

    setSeatQuantity((current) => Math.max(current, agencyPurchaseMinSeats));
  }, [isAgencyPurchase, agencyPurchaseMinSeats]);

  return (
    <ManageSubscriptionContextProvider
      contextValue={{
        seatQuantity,
        setSeatQuantity,
        pricing,
        setPricingPeriodInfo,
        initialState,
      }}
    >
      <ManageSubscription
        step={step}
        goToStep={goToStep}
        mixpanelSource={mixpanelSource}
      />
    </ManageSubscriptionContextProvider>
  );
}
